import React from 'react';
import {
  List,
  Show,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  useListContext,
  useShowContext,
  useDataProvider,
} from 'react-admin';
import { useNotificationTypes } from '../hooks/useNotificationTypes';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { CustomDataProvider } from '../types/dataProvider';
import {
  Bell,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Eye,
  MousePointer,
  BarChart3,
  RefreshCw,
  Pencil,
} from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { validateDateFormat } from '../utils/validators';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { RowActions } from '../components/ui/data-cells';
import { cn } from '../lib/utils';

/* ============================================================
   Notifications — Latio design system (derived). Analytics hero + list +
   detail + RA Create/Edit composer forms.
   ============================================================ */

const TARGET_SEGMENTS = [
  { id: 'all', name: 'All Users' },
  { id: 'active', name: 'Active Users' },
  { id: 'new', name: 'New Users' },
  { id: 'inactive', name: 'Inactive Users' },
];

/* status → tone */
const STATUS_TONE: Record<string, { cls: string; dot: string; Icon: any }> = {
  sent: { cls: 'bg-success-bg text-success', dot: 'bg-success', Icon: CheckCircle },
  sending: { cls: 'bg-indigo-tint text-indigo', dot: 'bg-indigo', Icon: Send },
  scheduled: { cls: 'bg-warning-bg text-warning', dot: 'bg-warning', Icon: Clock },
  draft: { cls: 'bg-surface-2 text-ink-2', dot: 'bg-ink-3', Icon: Bell },
  failed: { cls: 'bg-error-bg text-error', dot: 'bg-error', Icon: XCircle },
};
function NotifStatus({ status }: { status?: string }) {
  const s = (status || 'draft').toLowerCase();
  const m = STATUS_TONE[s] || STATUS_TONE.draft;
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-semibold capitalize', m.cls)}>
      <m.Icon className="size-3" />
      {s}
    </span>
  );
}

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';

/* ---- Analytics hero ---- */
const AnalyticsTile: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="rounded-input border border-hairline bg-background p-3">
    <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">{label}</div>
    <div className="mt-1 text-[20px] font-bold text-ink">{value ?? '—'}</div>
  </div>
);

const NotificationAnalytics: React.FC = () => {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.getNotificationAnalytics({ days });
      setData(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [dataProvider, days]);

  React.useEffect(() => {
    load();
  }, [load]);

  const pct = (v: number) => `${(v ?? 0).toFixed(1)}%`;

  return (
    <div className="rounded-card border border-hairline bg-background p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
          <BarChart3 className="size-4 text-ink-2" />
          Notification analytics
        </h3>
        <div className="flex items-center gap-2">
          <select
            className="rounded-input border border-hairline bg-background px-2 py-1 text-[13px] text-ink"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            disabled={loading}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
            className="inline-flex size-8 items-center justify-center rounded-pill border border-hairline text-ink-2 hover:text-ink disabled:opacity-50"
          >
            <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
          </button>
          {data && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex h-8 items-center rounded-pill border border-hairline px-3 text-[13px] font-semibold text-ink-2 hover:text-ink"
            >
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-error">{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
            <AnalyticsTile label="Sent" value={data.total_sent} />
            <AnalyticsTile label="Delivered" value={data.total_delivered} />
            <AnalyticsTile label="Opened" value={data.total_opened} />
            <AnalyticsTile label="Clicked" value={data.total_clicked} />
            <AnalyticsTile label="Delivery rate" value={pct(data.delivery_rate)} />
            <AnalyticsTile label="Open rate" value={pct(data.open_rate)} />
            <AnalyticsTile label="Click rate" value={pct(data.click_rate)} />
          </div>

          {expanded && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <h4 className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">Recent activity</h4>
                {(data.recent_activity || []).length === 0 ? (
                  <p className="text-sm text-ink-3">No data</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {(data.recent_activity || []).map((row: any) => (
                      <li key={row.date} className="flex justify-between">
                        <span className="text-ink-2">{row.date}</span>
                        <span className="text-ink">{row.sent ?? row.count ?? 0}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">Top types</h4>
                {(data.top_notification_types || []).length === 0 ? (
                  <p className="text-sm text-ink-3">No data</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {(data.top_notification_types || []).slice(0, 8).map((row: any, idx: number) => (
                      <li key={row.notification_type ?? idx} className="flex justify-between">
                        <span className="text-ink">{row.notification_type ?? row.type}</span>
                        <span className="text-ink-2">{row.count ?? row.total ?? 0}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">Device breakdown</h4>
                {data.device_breakdown && Object.keys(data.device_breakdown).length > 0 ? (
                  <ul className="space-y-1 text-sm">
                    {Object.entries(data.device_breakdown).map(([k, v]) => (
                      <li key={k} className="flex justify-between">
                        <span className="capitalize text-ink">{k}</span>
                        <span className="text-ink-2">{String(v)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-ink-3">No data</p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ---- List ---- */
const NOTIF_TABS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
];
const NOTIF_SORT = [
  { field: 'created_at', label: 'Created' },
  { field: 'title', label: 'Title' },
  { field: 'status', label: 'Status' },
  { field: 'id', label: 'ID' },
];
const NOTIF_SORT_LABELS = Object.fromEntries(NOTIF_SORT.map((f) => [f.field, f.label]));

const NotifStatusTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.status as string) || 'all';
  return (
    <SegmentedTabs
      tabs={NOTIF_TABS}
      value={current}
      onChange={(v) => setFilters({ ...filterValues, status: v === 'all' ? undefined : v }, displayedFilters)}
    />
  );
};

const NotificationsListBody = () => {
  const { getTypeName } = useNotificationTypes();
  const columns: DataColumn<any>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'title',
      header: 'Notification',
      sortable: true,
      cell: (r) => (
        <div className="min-w-0 max-w-[320px]">
          <div className="truncate font-semibold text-ink">{r.title}</div>
          {r.message && <div className="truncate text-[13px] text-ink-3">{r.message}</div>}
        </div>
      ),
    },
    { key: 'notification_type', header: 'Type', sortable: true, cell: (r) => <span className="text-ink-2">{getTypeName(r.notification_type)}</span> },
    { key: 'status', header: 'Status', sortable: true, cell: (r) => <NotifStatus status={r.status} /> },
    {
      key: 'audience',
      header: 'Audience',
      cell: (r) => (
        <span className="inline-flex items-center gap-1.5 text-ink-2">
          <Users className="size-3.5" />
          {r.target_criteria?.segments?.join(', ') || 'All users'}
          {r.target_count ? ` (${r.target_count})` : ''}
        </span>
      ),
    },
    {
      key: 'metrics',
      header: 'Metrics',
      cell: (r) =>
        r.delivery_stats ? (
          <div className="flex items-center gap-3 text-[13px] text-ink-2">
            {r.delivery_stats.total_sent != null && <span className="inline-flex items-center gap-1"><Send className="size-3" />{r.delivery_stats.total_sent}</span>}
            {r.delivery_stats.delivery_rate != null && <span className="inline-flex items-center gap-1"><CheckCircle className="size-3" />{r.delivery_stats.delivery_rate}%</span>}
            {r.delivery_stats.open_rate != null && <span className="inline-flex items-center gap-1"><Eye className="size-3" />{r.delivery_stats.open_rate}%</span>}
          </div>
        ) : (
          <span className="text-ink-3">—</span>
        ),
    },
    { key: 'created_at', header: 'Created', sortable: true, cell: (r) => fmtDate(r.created_at) },
    {
      key: 'actions',
      header: '',
      cell: (r) =>
        r.status === 'draft' || r.status === 'scheduled' ? (
          <RowActions>
            <a
              href={`#/notifications/${r.id}`}
              className="inline-flex size-8 items-center justify-center rounded-pill text-ink-2 hover:bg-surface-2 hover:text-ink"
              title="Edit"
            >
              <Pencil className="size-4" />
            </a>
          </RowActions>
        ) : null,
    },
  ];
  return (
    <>
      <div className="px-6 pt-6">
        <NotificationAnalytics />
      </div>
      <ListScreen
        tabs={<NotifStatusTabs />}
        actions={<SortMenu fields={NOTIF_SORT} />}
        tokens={<FilterTokens fields={[]} sortLabels={NOTIF_SORT_LABELS} />}
      >
        <DataTable columns={columns} rowClick="show" emptyMessage="No notifications found." />
        <div className="pt-1">
          <ReactAdminPagination />
        </div>
      </ListScreen>
    </>
  );
};

export const NotificationList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'created_at', order: 'DESC' }} perPage={25} component="div">
    <SearchIntegration filterKey="q" />
    <NotificationsListBody />
  </List>
);

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, children }: { title?: string; icon?: any; children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-hairline bg-background p-5">
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

const NotificationShowContent = () => {
  const { record, isLoading } = useShowContext();
  const { getTypeName } = useNotificationTypes();
  const { setData } = useBreadcrumbContext();

  React.useEffect(() => {
    if (record) setData({ resourceId: record.id.toString(), resourceName: record.title || `Notification #${record.id}`, resourceType: 'session' });
  }, [record, setData]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading notification…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Notification not found</div>;

  const ds = record.delivery_stats;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex size-[60px] items-center justify-center rounded-card bg-indigo-tint text-indigo">
            <Bell className="size-7" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                {record.title}
              </h1>
              <NotifStatus status={record.status} />
            </div>
            <p className="mt-1 text-sm text-ink-2">{getTypeName(record.notification_type)}</p>
          </div>
        </div>
        {(record.status === 'draft' || record.status === 'scheduled') && (
          <a
            href={`#/notifications/${record.id}`}
            className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
          >
            <Pencil className="size-4" />
            Edit
          </a>
        )}
      </div>

      {ds && (
        <StatRow>
          <StatCard primary label="Total sent" value={ds.total_sent || 0} />
          <StatCard label="Delivery rate" value={`${ds.delivery_rate || 0}%`} />
          <StatCard label="Open rate" value={`${ds.open_rate || 0}%`} />
          <StatCard label="Click rate" value={`${ds.click_rate || 0}%`} />
        </StatRow>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <DetailCard title="Message" icon={Bell}>
          <p className="whitespace-pre-wrap text-sm text-ink">{record.message}</p>
        </DetailCard>

        <div className="space-y-6">
          <DetailCard title="Audience" icon={Users}>
            <div className="divide-y divide-hairline">
              <Fact label="Segments" value={record.target_criteria?.segments?.join(', ') || 'All users'} />
              {record.target_count && <Fact label="Target count" value={`${record.target_count} users`} />}
            </div>
          </DetailCard>

          <DetailCard title="Timeline" icon={Clock}>
            <div className="divide-y divide-hairline">
              <Fact label="Created" value={record.created_at ? new Date(record.created_at).toLocaleString() : '—'} />
              {record.scheduled_for && <Fact label="Scheduled" value={new Date(record.scheduled_for).toLocaleString()} />}
              {record.sent_at && <Fact label="Sent" value={new Date(record.sent_at).toLocaleString()} />}
            </div>
          </DetailCard>

          {ds && (
            <DetailCard title="Engagement" icon={MousePointer}>
              <div className="divide-y divide-hairline">
                <Fact label="Opened" value={`${ds.open_rate || 0}%`} />
                <Fact label="Clicked" value={`${ds.click_rate || 0}%`} />
              </div>
            </DetailCard>
          )}
        </div>
      </div>
    </div>
  );
};

export const NotificationShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <NotificationShowContent />
  </Show>
);

/* ---- Edit / Create (RA composer forms) ---- */
export const NotificationEdit = (props: any) => {
  const { allChoices, loading } = useNotificationTypes();
  return (
    <Edit {...props}>
      <SimpleForm>
        <TextInput source="title" required fullWidth />
        <TextInput source="message" required multiline rows={4} fullWidth />
        <SelectInput source="notification_type" choices={allChoices} isLoading={loading} required />
        <SelectInput source="target_criteria.segments" choices={TARGET_SEGMENTS} defaultValue="all" />
        <TextInput source="scheduled_for" helperText="Format: YYYY-MM-DD HH:MM" validate={validateDateFormat('YYYY-MM-DD HH:MM')} />
      </SimpleForm>
    </Edit>
  );
};

export const NotificationCreate = (props: any) => {
  const { adminSendableChoices, loading } = useNotificationTypes();
  return (
    <Create {...props} redirect="show">
      <SimpleForm>
        <TextInput source="title" required fullWidth />
        <TextInput source="message" required multiline rows={4} fullWidth />
        <SelectInput source="notification_type" choices={adminSendableChoices} isLoading={loading} required defaultValue="admin_announcement" />
        <SelectInput source="target_criteria.segments" choices={TARGET_SEGMENTS} defaultValue="all" />
        <TextInput source="scheduled_for" helperText="Leave empty to send immediately. Format: YYYY-MM-DD HH:MM" validate={validateDateFormat('YYYY-MM-DD HH:MM')} />
      </SimpleForm>
    </Create>
  );
};
